import numbro from 'numbro';
import Format = numbro.Format;

export enum NumberFormatType {
  Currency = 'currency',
  Percentage = 'percentage',
}

interface NumberFormatOptions extends Format {
  type?: NumberFormatType;
  decimalPlaces?: number;
  thousandSeparated?: boolean;
  forceSign?: boolean;
}

export const formatNumber = (
  value: number,
  { type, decimalPlaces = 0, thousandSeparated = true, ...rest }: NumberFormatOptions = {},
) => {
  if (type === NumberFormatType.Currency) {
    const formattedValue = numbro(value).formatCurrency({
      thousandSeparated,
      mantissa: decimalPlaces,
      ...rest,
    });

    /**
     * There is a bug in numbro when formatting small negative currencies
     * when having no decimal places. The output is '-$' in these cases.
     * Manually convert to '$0'.
     * https://numbrojs.com/try-me.html?lang=en-US&value=-0.001&format=%7B%22mantissa%22%3A0%2C%22output%22%3A%22currency%22%2C%22lowPrecision%22%3Afalse%7D
     */
    return formattedValue === '-$' ? '$0' : formattedValue;
  }

  if (type === NumberFormatType.Percentage) {
    return numbro(value).format({
      output: 'percent',
      thousandSeparated,
      mantissa: decimalPlaces,
      ...rest,
    });
  }

  return numbro(value).format({
    thousandSeparated,
    mantissa: decimalPlaces,
    ...rest,
  });
};

export const formatCurrency = (value: number, options: Omit<NumberFormatOptions, 'type'> = {}) =>
  formatNumber(value, { type: NumberFormatType.Currency, ...options });

export const formatPercentage = (value: number, options: Omit<NumberFormatOptions, 'type'> = {}) =>
  formatNumber(value, { type: NumberFormatType.Percentage, ...options });

export const unFormatNumber = (value: string) => numbro.unformat(value);
